import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import type { FileModel, InformationRequestQuestion } from 'api';
import { ApiService } from 'api';
import type { RequestedFileFulfillmentStatus } from 'common/types/files/files';
import { VALUATION_TYPE_409A } from 'dashboard409a/common/constants/components';
import { useOnboardingEmailTemplates } from 'dashboard409a/hooks/useOnboardingEmailTemplates';
import { createIndividualDocument } from 'dashboard409a/services/dashboard';
import { RootStateType } from 'dashboard409a/states/store';
import FilesService from 'services/documents';
import { Logger } from 'utilities';
import {
  isOnboardingDocumentFulfilled,
  type OnboardingRequestedFile,
} from './OnboardingEmailModal.fulfillment';
import { OnboardingEmailModalProps } from './OnboardingEmailModal.props';
import { DocumentChecklistItem } from './OnboardingEmailModal.types';
import {
  composeOnboardingEmail,
  formatCombinedEmail,
  namesMatch,
  normalizeDocumentName,
  parseCombinedEmail,
} from './OnboardingEmailModal.utils';

const filesService = new FilesService();

type UseOnboardingEmailModalParams = Pick<
  OnboardingEmailModalProps,
  'open' | 'cmdId' | 'companyId' | 'companyName' | 'measurementDateId'
>;

// Map normalized RequestedFile.name -> request row for fulfillment and uploads.
const collectRequestedFilesByName = (
  files: OnboardingRequestedFile[] | undefined
): Map<string, OnboardingRequestedFile> => {
  const map = new Map<string, OnboardingRequestedFile>();
  (files ?? []).forEach(requestedFile => {
    map.set(normalizeDocumentName(requestedFile.name), requestedFile);
  });
  return map;
};

const collectUploadedFilesByName = (
  files: OnboardingRequestedFile[] | undefined
): Map<string, FileModel[]> => {
  const map = new Map<string, FileModel[]>();
  (files ?? []).forEach(requestedFile => {
    const uploadedFiles = requestedFile.files ?? [];
    const hasUploaded = Boolean(requestedFile.is_uploaded) || uploadedFiles.length > 0;
    if (hasUploaded && uploadedFiles.length > 0) {
      map.set(normalizeDocumentName(requestedFile.name), uploadedFiles);
    }
  });
  return map;
};

const toBackendQuestionChecklistItems = (
  questions: InformationRequestQuestion[] | undefined
): DocumentChecklistItem[] =>
  (questions ?? []).map(question => {
    // Show every child question (answered or not) with its answer when present.
    const childQuestions = (question.children_questions ?? []).map(child => ({
      label: child.name,
      answered: Boolean(child.answered),
      answerDisplay: child.answer_display?.trim() || null,
    }));
    return {
      label: question.name,
      name: question.name,
      description: question.description ?? undefined,
      kind: 'question' as const,
      checked: true,
      alreadyHave: Boolean(question.answered),
      answerDisplay: question.answer_display ?? null,
      childQuestions,
    };
  });

const useOnboardingEmailModal = ({
  open,
  cmdId,
  companyName,
}: UseOnboardingEmailModalParams) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const currentUserId = useSelector((state: RootStateType) => state.user.id);

  const {
    templates,
    presets,
    selectedId: selectedTemplateId,
    setSelectedId: setSelectedTemplateId,
    selectedTemplate,
    setEmailSubject,
    setIntroduction,
    savePresetOpen,
    presetName,
    setPresetName,
    handleOpenSavePreset,
    handleCloseSavePreset,
    handleSavePreset,
    handleApplyPreset,
    handleDeletePreset,
    handleResetToDefault,
  } = useOnboardingEmailTemplates();

  const [checklist, setChecklist] = useState<DocumentChecklistItem[]>([]);
  const [draftSubject, setDraftSubject] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [portalLink, setPortalLink] = useState<string | null>(null);
  const lastSavedEmailRef = useRef<string | null>(null);

  // Subject + body are edited separately but persisted as one combined string.
  const draftEmail = useMemo(
    () => formatCombinedEmail(draftSubject, draftBody),
    [draftSubject, draftBody]
  );

  const retrieveInformationRequestStatus = useCallback(async () => {
    if (!cmdId) {
      return null;
    }
    const existing = await ApiService.apiRetrieveInformationRequestStatusRead(String(cmdId));
    return existing.results[0] ?? null;
  }, [cmdId]);

  const { data: savedDraft, isLoading: isLoadingDocuments } = useQuery({
    queryKey: ['onboarding-email-saved-draft', cmdId],
    queryFn: async () => {
      if (!cmdId) {
        return null;
      }
      try {
        const [status, portal] = await Promise.all([
          retrieveInformationRequestStatus(),
          ApiService.apiTasksPortalLinksRead(String(cmdId)),
        ]);
        return {
          id: status?.id ?? null,
          draftedEmail: status?.drafted_email ?? '',
          portalLink: portal.document_portal_link ?? null,
          files: status?.files ?? [],
          questions: status?.questions ?? [],
        };
      } catch (error) {
        Logger.error('Failed to load saved onboarding email draft:', error);
        return {
          id: null,
          draftedEmail: '',
          portalLink: null,
          files: [] as OnboardingRequestedFile[],
          questions: [] as InformationRequestQuestion[],
        };
      }
    },
    enabled: open && Boolean(cmdId),
  });

  const requestedFilesByName = useMemo(
    () => collectRequestedFilesByName(savedDraft?.files),
    [savedDraft?.files]
  );
  const uploadedFilesByName = useMemo(
    () => collectUploadedFilesByName(savedDraft?.files),
    [savedDraft?.files]
  );
  const requestQuestions = useMemo(
    () => savedDraft?.questions ?? [],
    [savedDraft?.questions]
  );
  const isFollowUp = Boolean(savedDraft?.portalLink);

  useEffect(() => {
    if (!open || !selectedTemplate) {
      return;
    }

    const documentItems: DocumentChecklistItem[] = selectedTemplate.documents
      .filter(document => (document.kind ?? 'document') === 'document')
      .map(document => {
        const requestedFile = requestedFilesByName.get(normalizeDocumentName(document.name));
        const uploadedFiles =
          uploadedFilesByName.get(normalizeDocumentName(document.name)) ?? [];
        const alreadyHave =
          isOnboardingDocumentFulfilled(requestedFile) || uploadedFiles.length > 0;
        return {
          label: document.label,
          name: document.name,
          description: document.description,
          kind: 'document' as const,
          checked: !alreadyHave,
          alreadyHave,
          uploadedFiles,
          requestedFileId: requestedFile?.id,
          fulfillmentStatus: requestedFile?.fulfillment_status,
          fulfillmentNote: requestedFile?.fulfillment_note ?? null,
        };
      });

    const backendQuestionItems = toBackendQuestionChecklistItems(requestQuestions);
    const backendQuestionNames = backendQuestionItems.map(item => item.name);

    // Keep template-only questions that are not already represented on the request
    // (e.g. Term sheets when it is not a seeded default question).
    const unmatchedTemplateQuestions: DocumentChecklistItem[] = selectedTemplate.documents
      .filter(document => (document.kind ?? 'document') === 'question')
      .filter(
        document =>
          !backendQuestionNames.some(
            backendName =>
              namesMatch(backendName, document.name) || namesMatch(backendName, document.label)
          )
      )
      .map(document => ({
        label: document.label,
        name: document.name,
        description: document.description,
        kind: 'question' as const,
        checked: true,
        alreadyHave: false,
      }));

    setChecklist([...documentItems, ...backendQuestionItems, ...unmatchedTemplateQuestions]);
  }, [open, selectedTemplate, requestedFilesByName, uploadedFilesByName, requestQuestions]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (!cmdId) {
      setDraftSubject('');
      setDraftBody('');
      setPortalLink(null);
      lastSavedEmailRef.current = null;
      return;
    }
    if (savedDraft) {
      const { subject, body } = parseCombinedEmail(savedDraft.draftedEmail);
      setDraftSubject(subject);
      setDraftBody(body);
      setPortalLink(savedDraft.portalLink);
      // Compare against the normalized round-trip so loading a draft never
      // looks like an edit and re-triggers the autosave.
      lastSavedEmailRef.current = formatCombinedEmail(subject, body);
    }
  }, [open, cmdId, savedDraft]);

  const statusId = savedDraft?.id ?? null;

  const { mutate: persistDraftEmail } = useMutation({
    mutationFn: async (email: string) => {
      if (!statusId) {
        return null;
      }
      await ApiService.apiInformationRequestStatusDraftedEmailPartialUpdate(String(statusId), {
        id: statusId,
        drafted_email: email,
      });
      return email;
    },
    onSuccess: email => {
      if (email == null) {
        return;
      }
      lastSavedEmailRef.current = email;
      queryClient.setQueryData(
        ['onboarding-email-saved-draft', cmdId],
        (previous: typeof savedDraft) => (previous ? { ...previous, draftedEmail: email } : previous)
      );
    },
    onError: (error: Error) => {
      Logger.error('Failed to save onboarding email draft:', error);
      enqueueSnackbar('Failed to save your email edits.', { variant: 'error' });
    },
  });

  // Persist manual email edits immediately (used on blur / before closing).
  const flushDraftEmail = useCallback(() => {
    if (!statusId) {
      return;
    }
    if (draftEmail === lastSavedEmailRef.current) {
      return;
    }
    persistDraftEmail(draftEmail);
  }, [statusId, draftEmail, persistDraftEmail]);

  // Debounced autosave while the user is typing.
  useEffect(() => {
    if (!open || !statusId) {
      return undefined;
    }
    if (draftEmail === lastSavedEmailRef.current) {
      return undefined;
    }
    const handle = setTimeout(() => {
      persistDraftEmail(draftEmail);
    }, 800);
    return () => clearTimeout(handle);
  }, [open, statusId, draftEmail, persistDraftEmail]);

  const toggleDocument = useCallback((documentName: string) => {
    setChecklist(prev =>
      prev.map(item =>
        item.name === documentName && !item.alreadyHave
          ? { ...item, checked: !item.checked }
          : item
      )
    );
  }, []);

  const requestedDocuments = useMemo(
    () => checklist.filter(item => item.checked && !item.alreadyHave),
    [checklist]
  );

  const requestedPortalDocuments = useMemo(
    () => requestedDocuments.filter(item => (item.kind ?? 'document') === 'document'),
    [requestedDocuments]
  );

  const ensureInformationRequestStatus = useCallback(async () => {
    if (!cmdId) {
      throw new Error('Missing company measurement date');
    }

    const existing = await retrieveInformationRequestStatus();
    if (existing?.id) {
      return existing;
    }

    // Skip firm/company document-template seeding so this request contains
    // only the documents selected from the client-side onboarding template.
    return ApiService.apiCreateInformationRequestStatusCreate({
      report_date: cmdId,
      valuation_type: VALUATION_TYPE_409A,
      seed_documents_from_templates: false,
    });
  }, [cmdId, retrieveInformationRequestStatus]);

  const { mutateAsync: markDocumentFulfillment, isPending: isMarkingDocumentNa } = useMutation({
    mutationFn: async ({
      documentName,
      fulfillmentStatus,
      fulfillmentNote,
      requestedFileId,
    }: {
      documentName: string;
      fulfillmentStatus: RequestedFileFulfillmentStatus;
      fulfillmentNote?: string | null;
      requestedFileId?: number;
    }) => {
      const status = await ensureInformationRequestStatus();
      if (!status.id) {
        throw new Error('Failed to resolve information request status');
      }

      let resolvedRequestedFileId = requestedFileId;
      if (!resolvedRequestedFileId) {
        const created = await ApiService.apiDocumentsRequestedFileCreate({
          name: documentName,
          company_information_request_status: status.id,
        });
        resolvedRequestedFileId = created.id;
      }
      if (!resolvedRequestedFileId) {
        throw new Error('Failed to resolve requested document');
      }

      await filesService.markRequestedFileFulfillment(resolvedRequestedFileId, {
        fulfillment_status: fulfillmentStatus,
        fulfillment_note: fulfillmentNote ?? null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-email-saved-draft', cmdId] });
      enqueueSnackbar('Document marked successfully.', { variant: 'success' });
    },
    onError: (error: Error) => {
      Logger.error('Failed to mark onboarding document N/A:', error);
      enqueueSnackbar(error.message || 'Failed to update document status.', { variant: 'error' });
    },
  });

  const markDocumentAsNa = useCallback(
    async (
      item: DocumentChecklistItem,
      fulfillmentStatus: RequestedFileFulfillmentStatus,
      fulfillmentNote?: string | null
    ) => {
      if (item.kind !== 'document' || item.alreadyHave) {
        return;
      }
      await markDocumentFulfillment({
        documentName: item.name,
        fulfillmentStatus,
        fulfillmentNote,
        requestedFileId: item.requestedFileId,
      });
    },
    [markDocumentFulfillment]
  );

  const { mutate: generateEmail, isPending: isGenerating } = useMutation({
    mutationFn: async () => {
      if (!cmdId) {
        throw new Error('Missing company measurement date');
      }
      if (!selectedTemplate) {
        throw new Error('No onboarding template selected');
      }
      if (!currentUserId) {
        throw new Error('Missing current user');
      }
      // First send requires at least one still-needed document. Follow-up
      // regenerate can run even when every document is already provided.
      if (!isFollowUp && requestedPortalDocuments.length === 0) {
        throw new Error('Select at least one document that still needs to be requested');
      }

      const status = await ensureInformationRequestStatus();
      if (!status.id) {
        throw new Error('Failed to resolve information request status');
      }

      const existingNames = new Set(
        (status.files ?? []).map(file => normalizeDocumentName(file.name))
      );

      // Push selected still-needed docs that are not already on the draft status.
      // Questions are email/checklist-only and are not created as RequestedFile rows.
      const documentsToCreate = requestedPortalDocuments.filter(
        document => !existingNames.has(normalizeDocumentName(document.name))
      );
      await Promise.all(
        documentsToCreate.map(document =>
          createIndividualDocument({
            name: document.name,
            company_information_request_status: status.id as number,
          })
        )
      );

      const existingPortal = await ApiService.apiTasksPortalLinksRead(String(cmdId));
      const hasExistingRequest = Boolean(existingPortal.document_portal_link);

      if (hasExistingRequest) {
        await ApiService.apiUpdateInformationRequestStatusUpdate(String(status.id), {
          id: status.id,
          responsible_users: [currentUserId],
          send_email: false,
        });
      } else {
        await ApiService.apiCreateInformationRequestUpdate(String(status.id), {
          responsible_users: [currentUserId],
          send_email: false,
        });
      }

      const portal = await ApiService.apiTasksPortalLinksRead(String(cmdId));
      const documentPortalLink = portal.document_portal_link;
      if (!documentPortalLink) {
        throw new Error('Document request portal link was not created');
      }

      // Email body matches the client-side template selection exactly
      // (checked + not already provided), including per-document descriptions.
      const email = composeOnboardingEmail({
        template: selectedTemplate,
        companyName,
        portalLink: documentPortalLink,
        requestedDocuments,
      });

      await ApiService.apiInformationRequestStatusDraftedEmailPartialUpdate(String(status.id), {
        id: status.id,
        drafted_email: email,
      });

      return {
        portalLink: documentPortalLink,
        email,
        statusId: status.id,
      };
    },
    onSuccess: result => {
      setPortalLink(result.portalLink);
      const { subject, body } = parseCombinedEmail(result.email);
      setDraftSubject(subject);
      setDraftBody(body);
      lastSavedEmailRef.current = result.email;
      // Refetch so checklist picks up seeded request questions + upload state.
      queryClient.invalidateQueries({ queryKey: ['onboarding-email-saved-draft', cmdId] });
      enqueueSnackbar('Onboarding email drafted with document request portal link.', {
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      Logger.error('Failed to generate onboarding email:', error);
      enqueueSnackbar(error.message || 'Failed to generate onboarding email. Please try again.', {
        variant: 'error',
      });
    },
  });

  const handleCopyEmail = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(draftEmail);
      enqueueSnackbar('Email copied to clipboard', { variant: 'success' });
    } catch (error) {
      Logger.error('Failed to copy email:', error);
      enqueueSnackbar('Failed to copy email to clipboard.', { variant: 'error' });
    }
  }, [draftEmail, enqueueSnackbar]);

  const handleCopyPortalLink = useCallback(async () => {
    if (!portalLink) {
      return;
    }
    try {
      await navigator.clipboard.writeText(portalLink);
      enqueueSnackbar('Portal link copied to clipboard', { variant: 'success' });
    } catch (error) {
      Logger.error('Failed to copy portal link:', error);
      enqueueSnackbar('Failed to copy portal link to clipboard.', { variant: 'error' });
    }
  }, [portalLink, enqueueSnackbar]);

  const canGenerate =
    Boolean(cmdId) &&
    Boolean(selectedTemplate) &&
    Boolean(currentUserId) &&
    (isFollowUp || requestedPortalDocuments.length > 0) &&
    !isGenerating;

  return {
    templates,
    presets,
    selectedTemplateId,
    setSelectedTemplateId,
    selectedTemplate,
    emailSubject: selectedTemplate?.emailSubject ?? '',
    introduction: selectedTemplate?.introduction ?? '',
    setEmailSubject,
    setIntroduction,
    savePresetOpen,
    presetName,
    setPresetName,
    handleOpenSavePreset,
    handleCloseSavePreset,
    handleSavePreset,
    handleApplyPreset,
    handleDeletePreset,
    handleResetToDefault,
    checklist,
    toggleDocument,
    markDocumentAsNa,
    isMarkingDocumentNa,
    isLoadingDocuments,
    draftEmail,
    draftSubject,
    setDraftSubject,
    draftBody,
    setDraftBody,
    flushDraftEmail,
    portalLink,
    generateEmail,
    isGenerating,
    canGenerate,
    isFollowUp,
    handleCopyEmail,
    handleCopyPortalLink,
    requestedDocuments,
  };
};

export default useOnboardingEmailModal;
